import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const AIMachineLearning = () => {
  return (
    <>
      <SEO 
        title="AI & Machine Learning Services - Enterprise AI Solutions"
        description="Transform your business with cutting-edge AI and machine learning solutions. Custom models, predictive analytics, intelligent automation, and ML operations for enterprise success."
        keywords="AI services, machine learning, artificial intelligence, predictive analytics, deep learning, neural networks, ML models, AI consulting, enterprise AI"
      />

      {/* Hero Section - Full Width */}
      <section className="relative overflow-hidden bg-gradient-to-br from-pearl-white via-alabaster to-white pt-16 pb-12">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-blush-light/40 rounded-full mb-6 animate-fade-in">
              <span className="text-primary-red font-semibold text-sm">Enterprise AI & Machine Learning</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold leading-tight mb-6 text-charcoal animate-slide-up">
              Transform Your Business with <span className="text-gradient">Intelligent AI Solutions</span>
            </h1>
            <p className="text-xl md:text-2xl text-titanium mb-10 leading-relaxed animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Harness the power of artificial intelligence to automate processes, gain predictive insights, and drive unprecedented innovation across your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/contact" className="btn-primary text-center text-lg px-10 py-4">
                Start Your AI Journey
              </Link>
              <Link to="/ai-hub" className="btn-secondary text-center text-lg px-10 py-4">
                Explore AI Hub
              </Link>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              {[
                { value: '95%+', label: 'Model Accuracy' },
                { value: '10x', label: 'Faster Processing' },
                { value: '40-60%', label: 'Cost Reduction' },
                { value: '100+', label: 'AI Projects Delivered' }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-primary-red mb-2">{stat.value}</div>
                  <div className="text-sm text-titanium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: Custom ML Models - Content Left, Visual Right */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-slide-up">
              <div className="inline-block px-4 py-2 bg-blush-light/40 rounded-full mb-4">
                <span className="text-primary-red font-semibold text-sm">Custom AI Development</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-charcoal">
                Bespoke Machine Learning Models Tailored to Your Data
              </h2>
              <p className="text-lg text-titanium mb-6 leading-relaxed">
                We develop custom machine learning models specifically trained on your unique datasets to solve your most pressing business challenges. Our expert data scientists and ML engineers work closely with your team to understand your requirements and deliver solutions that drive measurable results.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  'End-to-end model development from concept to production',
                  'Feature engineering and data optimization',
                  'Continuous model monitoring and improvement',
                  'Seamless integration with existing systems'
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-6 h-6 bg-primary-red rounded-full flex items-center justify-center flex-shrink-0 mt-1 mr-3">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-titanium">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact" className="btn-primary inline-block">
                Discuss Your Project
              </Link>
            </div>
            <div className="animate-fade-in">
              <div className="bg-alabaster rounded-2xl p-8 shadow-lg">
                <div className="space-y-4">
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-platinum">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-primary-red rounded-lg flex items-center justify-center mr-4">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-charcoal">Supervised Learning</div>
                        <div className="text-sm text-titanium">Classification & Regression</div>
                      </div>
                    </div>
                    <div className="w-full bg-platinum rounded-full h-2">
                      <div className="bg-primary-red h-2 rounded-full" style={{ width: '92%' }}></div>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-platinum">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-primary-red rounded-lg flex items-center justify-center mr-4">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-charcoal">Unsupervised Learning</div>
                        <div className="text-sm text-titanium">Clustering & Anomaly Detection</div>
                      </div>
                    </div>
                    <div className="w-full bg-platinum rounded-full h-2">
                      <div className="bg-primary-red h-2 rounded-full" style={{ width: '88%' }}></div>
                    </div>
                  </div>
                  <div className="bg-white p-6 rounded-xl shadow-sm border border-platinum">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-primary-red rounded-lg flex items-center justify-center mr-4">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-lg font-semibold text-charcoal">Reinforcement Learning</div>
                        <div className="text-sm text-titanium">Adaptive Decision Systems</div>
                      </div>
                    </div>
                    <div className="w-full bg-platinum rounded-full h-2">
                      <div className="bg-primary-red h-2 rounded-full" style={{ width: '85%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Predictive Analytics - Visual Left, Content Right */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1 animate-fade-in">
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-platinum">
                <h3 className="text-2xl font-bold mb-6 text-charcoal">Predictive Power Across Industries</h3>
                <div className="space-y-6">
                  {[
                    {
                      industry: 'Healthcare',
                      metric: 'Patient Outcome Accuracy',
                      value: '94%',
                      icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z'
                    },
                    {
                      industry: 'Finance',
                      metric: 'Fraud Detection Rate',
                      value: '98%',
                      icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                    },
                    {
                      industry: 'Retail',
                      metric: 'Demand Forecast Precision',
                      value: '91%',
                      icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z'
                    },
                    {
                      industry: 'Manufacturing',
                      metric: 'Maintenance Prediction',
                      value: '96%',
                      icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z'
                    }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-alabaster rounded-lg">
                      <div className="flex items-center flex-1">
                        <div className="w-10 h-10 bg-primary-red/10 rounded-lg flex items-center justify-center mr-4">
                          <svg className="w-5 h-5 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                          </svg>
                        </div>
                        <div>
                          <div className="font-semibold text-charcoal text-sm">{item.industry}</div>
                          <div className="text-xs text-titanium">{item.metric}</div>
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-primary-red">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2 animate-slide-up">
              <div className="inline-block px-4 py-2 bg-blush-light/40 rounded-full mb-4">
                <span className="text-primary-red font-semibold text-sm">Predictive Intelligence</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-charcoal">
                Forecast the Future with Advanced Predictive Analytics
              </h2>
              <p className="text-lg text-titanium mb-6 leading-relaxed">
                Leverage sophisticated algorithms and statistical models to anticipate trends, predict customer behavior, and make data-driven decisions that give you a competitive edge. Our predictive analytics solutions turn your historical data into actionable foresight.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { label: 'Time Series Forecasting', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
                  { label: 'Customer Churn Prediction', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
                  { label: 'Risk Assessment', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
                  { label: 'Demand Forecasting', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center p-3 bg-white rounded-lg border border-platinum">
                    <svg className="w-5 h-5 text-primary-red mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.icon} />
                    </svg>
                    <span className="text-sm text-charcoal font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
              <Link to="/ai-hub/predictive-analytics" className="text-primary-red font-semibold hover:underline">
                Explore Predictive Analytics →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: NLP & Computer Vision - Content Left, Visual Right */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-slide-up">
              <div className="inline-block px-4 py-2 bg-blush-light/40 rounded-full mb-4">
                <span className="text-primary-red font-semibold text-sm">Intelligent Understanding</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-charcoal">
                Natural Language Processing & Computer Vision
              </h2>
              <p className="text-lg text-titanium mb-6 leading-relaxed">
                Enable your systems to understand human language and interpret visual information with state-of-the-art NLP and computer vision technologies. From chatbots to image recognition, we build AI that sees, reads, and comprehends like humans.
              </p>
              
              <div className="space-y-6">
                <div className="bg-alabaster p-6 rounded-xl">
                  <div className="flex items-start mb-3">
                    <div className="w-10 h-10 bg-primary-red rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-charcoal mb-2">Natural Language Processing</h3>
                      <ul className="space-y-2 text-titanium">
                        <li className="flex items-start">
                          <span className="text-primary-red mr-2">•</span>
                          <span>Intelligent chatbots and virtual assistants</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-primary-red mr-2">•</span>
                          <span>Sentiment analysis and opinion mining</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-primary-red mr-2">•</span>
                          <span>Document classification and summarization</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-primary-red mr-2">•</span>
                          <span>Language translation and text generation</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-alabaster p-6 rounded-xl">
                  <div className="flex items-start mb-3">
                    <div className="w-10 h-10 bg-primary-red rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-charcoal mb-2">Computer Vision</h3>
                      <ul className="space-y-2 text-titanium">
                        <li className="flex items-start">
                          <span className="text-primary-red mr-2">•</span>
                          <span>Image classification and object detection</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-primary-red mr-2">•</span>
                          <span>Facial recognition and biometric systems</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-primary-red mr-2">•</span>
                          <span>Quality control and defect detection</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-primary-red mr-2">•</span>
                          <span>Video analysis and action recognition</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <Link to="/ai-hub/nlp" className="text-primary-red font-semibold hover:underline">
                  NLP Solutions →
                </Link>
                <Link to="/ai-hub/computer-vision" className="text-primary-red font-semibold hover:underline">
                  Computer Vision →
                </Link>
              </div>
            </div>
            <div className="animate-fade-in">
              <div className="bg-gradient-to-br from-alabaster to-pearl-white rounded-2xl p-8 shadow-lg">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: 'Text Analysis', value: '98%', subtitle: 'Accuracy' },
                    { label: 'Image Recognition', value: '96%', subtitle: 'Precision' },
                    { label: 'Language Models', value: '50+', subtitle: 'Languages' },
                    { label: 'Real-time Processing', value: '<100ms', subtitle: 'Latency' },
                  ].map((stat, index) => (
                    <div key={index} className="bg-white p-6 rounded-xl shadow-sm border border-platinum text-center">
                      <div className="text-3xl font-bold text-primary-red mb-1">{stat.value}</div>
                      <div className="text-sm font-semibold text-charcoal mb-1">{stat.label}</div>
                      <div className="text-xs text-titanium">{stat.subtitle}</div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6 p-6 bg-white rounded-xl border border-platinum">
                  <h4 className="font-semibold text-charcoal mb-4">Pre-trained Models Available</h4>
                  <div className="flex flex-wrap gap-2">
                    {['BERT', 'GPT', 'ResNet', 'YOLO', 'Transformer', 'VGG', 'EfficientNet', 'T5'].map((model, index) => (
                      <span key={index} className="px-3 py-1 bg-blush-light/30 text-primary-red text-sm rounded-full">
                        {model}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Technology Stack - Full Width */}
      <section className="py-5 md:py-5 bg-charcoal text-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="inline-block px-4 py-2 bg-primary-red/20 rounded-full mb-4">
              <span className="text-coral-mist font-semibold text-sm">Enterprise-Grade Technology</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Built on Industry-Leading AI Frameworks
            </h2>
            <p className="text-lg text-cool-grey leading-relaxed">
              We leverage the most powerful and proven technologies to build scalable, production-ready AI solutions
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                category: 'ML Frameworks',
                items: ['TensorFlow', 'PyTorch', 'Scikit-learn', 'Keras', 'XGBoost']
              },
              {
                category: 'Deep Learning',
                items: ['CUDA', 'cuDNN', 'ONNX', 'OpenCV', 'FastAI']
              },
              {
                category: 'Cloud Platforms',
                items: ['AWS SageMaker', 'Google Cloud AI', 'Azure ML', 'Databricks']
              },
              {
                category: 'MLOps Tools',
                items: ['MLflow', 'Kubeflow', 'DVC', 'Airflow', 'Docker']
              }
            ].map((stack, index) => (
              <div key={index} className="bg-graphite rounded-xl p-6 border border-titanium/30">
                <h3 className="text-lg font-semibold mb-4 text-coral-mist">{stack.category}</h3>
                <div className="space-y-2">
                  {stack.items.map((item, idx) => (
                    <div key={idx} className="py-2 px-3 bg-charcoal rounded-lg text-cool-grey text-sm hover:text-white hover:bg-titanium/20 transition-all duration-300 cursor-default">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-cool-grey mb-6">And many more cutting-edge tools and frameworks</p>
            <Link to="/contact" className="bg-primary-red text-white px-8 py-4 rounded-lg font-semibold hover:bg-deep-garnet transition-all duration-300 inline-block">
              Discuss Your Technology Needs
            </Link>
          </div>
        </div>
      </section>

      {/* Section 5: Development Process - Visual Left, Content Right */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1 animate-fade-in">
              <div className="relative">
                {[
                  {
                    step: '01',
                    title: 'Discovery & Assessment',
                    description: 'Understand business objectives, evaluate data readiness, identify AI opportunities',
                    icon: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  },
                  {
                    step: '02',
                    title: 'Data Preparation',
                    description: 'Clean, transform, and engineer features for optimal model performance',
                    icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4'
                  },
                  {
                    step: '03',
                    title: 'Model Development',
                    description: 'Build, train, and optimize ML models using appropriate algorithms',
                    icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z'
                  },
                  {
                    step: '04',
                    title: 'Deployment & Monitoring',
                    description: 'Deploy to production, establish monitoring, enable continuous improvement',
                    icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z'
                  }
                ].map((process, index) => (
                  <div key={index} className="relative mb-8 last:mb-0">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-primary-red rounded-xl flex items-center justify-center">
                          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={process.icon} />
                          </svg>
                        </div>
                      </div>
                      <div className="ml-6 flex-1">
                        <div className="text-5xl font-bold text-primary-red/10 absolute top-0 right-0">{process.step}</div>
                        <h3 className="text-xl font-semibold text-charcoal mb-2">{process.title}</h3>
                        <p className="text-titanium">{process.description}</p>
                      </div>
                    </div>
                    {index < 3 && (
                      <div className="absolute left-8 top-20 w-0.5 h-12 bg-platinum"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2 animate-slide-up">
              <div className="inline-block px-4 py-2 bg-blush-light/40 rounded-full mb-4">
                <span className="text-primary-red font-semibold text-sm">Proven Methodology</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-charcoal">
                Structured AI Development Process
              </h2>
              <p className="text-lg text-titanium mb-6 leading-relaxed">
                Our systematic approach ensures successful AI implementation from initial concept through production deployment and beyond. We follow industry best practices and proven methodologies to deliver results on time and within budget.
              </p>
              
              <div className="bg-alabaster rounded-xl p-6 mb-6">
                <h3 className="font-semibold text-charcoal mb-4">Average Project Timeline</h3>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-titanium">POC Development</span>
                      <span className="text-primary-red font-semibold">4-6 weeks</span>
                    </div>
                    <div className="w-full bg-platinum rounded-full h-2">
                      <div className="bg-coral-mist h-2 rounded-full" style={{ width: '35%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-titanium">Full Implementation</span>
                      <span className="text-primary-red font-semibold">3-6 months</span>
                    </div>
                    <div className="w-full bg-platinum rounded-full h-2">
                      <div className="bg-primary-red h-2 rounded-full" style={{ width: '65%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-titanium">Enterprise Rollout</span>
                      <span className="text-primary-red font-semibold">6-12 months</span>
                    </div>
                    <div className="w-full bg-platinum rounded-full h-2">
                      <div className="bg-deep-garnet h-2 rounded-full" style={{ width: '90%' }}></div>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-titanium mb-6">
                We also offer rapid prototyping services to validate AI concepts in as little as 2-3 weeks, helping you make informed decisions before full-scale investment.
              </p>

              <Link to="/contact" className="btn-primary inline-block">
                Start Your Project
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Benefits - Full Width Cards */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="inline-block px-4 py-2 bg-blush-light/40 rounded-full mb-4">
              <span className="text-primary-red font-semibold text-sm">Why Choose Abacies</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-charcoal">
              The Abacies AI Advantage
            </h2>
            <p className="text-lg text-titanium leading-relaxed">
              Partner with AI experts who deliver measurable business value
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {[
              {
                icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
                title: 'Proven Track Record',
                description: '12+ years delivering successful AI projects with 99% client satisfaction rate'
              },
              {
                icon: 'M13 10V3L4 14h7v7l9-11h-7z',
                title: 'Accelerated Time-to-Value',
                description: 'Rapid prototyping and deployment to see results in weeks, not years'
              },
              {
                icon: 'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4',
                title: 'Enterprise Scalability',
                description: 'Solutions that grow with your business, handling millions of transactions'
              },
              {
                icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
                title: 'Responsible AI',
                description: 'Ethical AI development with built-in fairness, transparency, and explainability'
              },
              {
                icon: 'M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.5v7M12 14.5v7M2.5 12h7M14.5 12h7',
                title: 'Full-Stack Expertise',
                description: 'End-to-end capabilities from data science to production engineering'
              },
              {
                icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                title: 'Demonstrable ROI',
                description: '40-60% cost reduction and 10x processing improvements on average'
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-all duration-300 border border-platinum">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={benefit.icon} />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal mb-2">{benefit.title}</h3>
                    <p className="text-titanium">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7: Use Cases - Content Left, Visual Right */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-slide-up">
              <div className="inline-block px-4 py-2 bg-blush-light/40 rounded-full mb-4">
                <span className="text-primary-red font-semibold text-sm">Real-World Applications</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-charcoal">
                AI Transforming Industries Worldwide
              </h2>
              <p className="text-lg text-titanium mb-8 leading-relaxed">
                From healthcare to finance, manufacturing to retail, our AI solutions are driving measurable business outcomes across diverse sectors.
              </p>

              <div className="space-y-4">
                {[
                  {
                    industry: 'Healthcare & Life Sciences',
                    impact: '30% faster diagnosis',
                    cases: ['Medical image analysis', 'Patient outcome prediction', 'Drug discovery acceleration']
                  },
                  {
                    industry: 'Financial Services',
                    impact: '99% fraud detection',
                    cases: ['Real-time fraud prevention', 'Credit risk assessment', 'Algorithmic trading optimization']
                  },
                  {
                    industry: 'Manufacturing',
                    impact: '45% reduced downtime',
                    cases: ['Predictive maintenance', 'Quality control automation', 'Supply chain optimization']
                  },
                  {
                    industry: 'Retail & E-commerce',
                    impact: '25% revenue increase',
                    cases: ['Personalized recommendations', 'Dynamic pricing', 'Inventory optimization']
                  }
                ].map((useCase, index) => (
                  <div key={index} className="bg-alabaster rounded-xl p-6 hover:shadow-lg transition-all duration-300">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-lg font-semibold text-charcoal">{useCase.industry}</h3>
                      <span className="px-3 py-1 bg-primary-red text-white text-sm rounded-full font-semibold">
                        {useCase.impact}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {useCase.cases.map((item, idx) => (
                        <span key={idx} className="text-sm text-titanium bg-white px-3 py-1 rounded-lg">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Link to="/solutions" className="text-primary-red font-semibold hover:underline">
                  View All Industry Solutions →
                </Link>
              </div>
            </div>
            <div className="animate-fade-in">
              <div className="bg-gradient-to-br from-primary-red to-deep-garnet rounded-2xl p-8 text-white shadow-xl">
                <h3 className="text-2xl font-bold mb-6">AI Impact Across Sectors</h3>
                <div className="space-y-6">
                  {[
                    { metric: 'Operational Efficiency', value: '60%', description: 'Average improvement' },
                    { metric: 'Cost Reduction', value: '$2.5M', description: 'Annual savings per client' },
                    { metric: 'Accuracy Rate', value: '95%+', description: 'Model performance' },
                    { metric: 'Processing Speed', value: '10x', description: 'Faster than manual' }
                  ].map((stat, index) => (
                    <div key={index} className="border-b border-white/20 pb-4 last:border-0">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm opacity-90">{stat.metric}</span>
                        <span className="text-2xl font-bold">{stat.value}</span>
                      </div>
                      <p className="text-xs opacity-75">{stat.description}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-white/20">
                  <p className="text-sm opacity-90 mb-4">Ready to achieve similar results?</p>
                  <Link to="/contact" className="bg-white text-primary-red px-6 py-3 rounded-lg font-semibold hover:bg-pearl-white transition-all duration-300 inline-block">
                    Get Started Today
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Full Width */}
      <section className="py-5 md:py-5 bg-gradient-to-r from-primary-red to-deep-garnet text-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-6xl font-bold mb-6">
              Ready to Transform Your Business with AI?
            </h2>
            <p className="text-xl md:text-2xl mb-10 opacity-90 leading-relaxed">
              Let's discuss how our AI & machine learning solutions can drive innovation, reduce costs, and create lasting competitive advantages for your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact" 
                className="bg-white text-primary-red px-10 py-5 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg"
              >
                Schedule Free Consultation
              </Link>
              <Link 
                to="/ai-hub" 
                className="border-2 border-white text-white px-10 py-5 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300"
              >
                Explore AI Capabilities
              </Link>
            </div>
            <p className="text-sm opacity-75 mt-8">
              Join 100+ companies already leveraging AI for business transformation
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default AIMachineLearning;